import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Route, X } from 'lucide-react';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { FLOATING_KEYBOARD_ANIM_MS } from '../../utils/learnerSlideModalMotion';

const PANEL_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;
const GAP_ABOVE_ANCHOR_PX = 8;

export interface LessonNotPassedModalProps {
  open: boolean;
  /** `rtl` for Persian — panel slides in from the left; `ltr` slides in from the right. */
  isRtl: boolean;
  /** Next-lesson button — panel is positioned directly above it. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  onDismiss: () => void;
  onContinue: (dontShowAgain: boolean) => void;
  onViewJourney: () => void;
}

type AnchorRect = {
  top: number;
  left: number;
  right: number;
  width: number;
};

/**
 * Compact slide-in hint before the learner continues to the next lesson without passing.
 */
export const LessonNotPassedModal: React.FC<LessonNotPassedModalProps> = ({
  open,
  isRtl,
  anchorRef,
  onDismiss,
  onContinue,
  onViewJourney
}) => {
  const { t } = useTranslation('learning');
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActionRef = useRef<'dismiss' | 'continue' | 'journey' | null>(null);
  const dontShowAgainRef = useRef(false);

  const updateAnchorRect = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) {
      setAnchorRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setAnchorRect({
      top: rect.top,
      left: rect.left,
      right: rect.right,
      width: rect.width
    });
  }, [anchorRef]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
      setDontShowAgain(false);
      pendingActionRef.current = null;
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !mounted) return;
    updateAnchorRect();
    const raf = window.requestAnimationFrame(updateAnchorRect);
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [open, mounted, updateAnchorRect]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  const finishExit = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setMounted(false);
    setExiting(false);

    if (action === 'continue') {
      onContinue(dontShowAgainRef.current);
    } else if (action === 'journey') {
      onViewJourney();
    } else {
      onDismiss();
    }
  }, [onContinue, onDismiss, onViewJourney]);

  const startExit = useCallback(
    (action: 'dismiss' | 'continue' | 'journey') => {
      if (exiting) return;
      pendingActionRef.current = action;
      if (prefersReducedMotion()) {
        finishExit();
        return;
      }
      setExiting(true);
      exitTimerRef.current = setTimeout(finishExit, PANEL_ANIM_MS);
    },
    [exiting, finishExit]
  );

  useHistoryBackClose(open, () => startExit('dismiss'), 'lesson-not-passed');

  if (!mounted) return null;

  const slideClass = exiting
    ? isRtl
      ? 'lesson-not-passed-hint-exit-rtl'
      : 'lesson-not-passed-hint-exit-ltr'
    : isRtl
      ? 'lesson-not-passed-hint-enter-rtl'
      : 'lesson-not-passed-hint-enter-ltr';

  const panelWidth = Math.min(352, Math.max(anchorRect?.width ?? 260, 260), window.innerWidth - 24);
  const anchoredStyle: React.CSSProperties | undefined = anchorRect
    ? isRtl
      ? {
          position: 'fixed',
          top: anchorRect.top - GAP_ABOVE_ANCHOR_PX,
          left: anchorRect.left,
          width: panelWidth,
          transform: 'translateY(-100%)',
          zIndex: 110
        }
      : {
          position: 'fixed',
          top: anchorRect.top - GAP_ABOVE_ANCHOR_PX,
          left: anchorRect.right - panelWidth,
          width: panelWidth,
          transform: 'translateY(-100%)',
          zIndex: 110
        }
    : undefined;

  return (
    <div
      className={
        anchoredStyle
          ? 'pointer-events-none'
          : `pointer-events-none fixed top-[4.5rem] z-[110] w-[min(100vw-1.5rem,22rem)] sm:top-20 ${
              isRtl ? 'start-3 sm:start-4' : 'end-3 sm:end-4'
            }`
      }
      style={anchoredStyle}
      role="presentation"
    >
      <div
        className={`lesson-not-passed-hint-panel pointer-events-auto overflow-hidden rounded-xl border border-amber-200/80 bg-white/95 shadow-lg shadow-amber-900/10 backdrop-blur-sm dark:border-amber-800/45 dark:bg-gray-900/95 dark:shadow-black/30 ${slideClass}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="lesson-not-passed-title"
        aria-describedby="lesson-not-passed-desc"
      >
        <div className="relative px-3.5 pb-3 pt-3 sm:px-4 sm:pb-3.5 sm:pt-3.5">
          <button
            type="button"
            onClick={() => startExit('dismiss')}
            className="absolute end-2 top-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label={t('lessonNotPassed.dismiss')}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>

          <div className="flex gap-2.5 pe-6">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
              <Route className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p
                id="lesson-not-passed-title"
                className="text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50"
              >
                {t('lessonNotPassed.title')}
              </p>
              <p
                id="lesson-not-passed-desc"
                className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300"
              >
                {t('lessonNotPassed.message')}
              </p>
            </div>
          </div>

          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg px-0.5 py-0.5 text-xs text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <span>{t('lessonNotPassed.dontShowAgain')}</span>
          </label>

          <div className="mt-3 flex flex-col gap-1.5 sm:flex-row">
            <button
              type="button"
              onClick={() => startExit('journey')}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-800 transition-colors hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-950/40 dark:text-indigo-100 dark:hover:bg-indigo-950/60"
            >
              <Route className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
              {t('lessonNotPassed.viewJourney')}
            </button>
            <button
              type="button"
              onClick={() => {
                dontShowAgainRef.current = dontShowAgain;
                startExit('continue');
              }}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              {t('lessonNotPassed.continueNext')}
              <ArrowRight
                className={localeMirroredNavArrowClass(isRtl, 'h-3.5 w-3.5 shrink-0')}
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
